import {
    getNotificationGetMyNotificationTypePreferences,
    postNotificationSaveNotificationRecipients,
    postNotificationSaveNotificationTypePreference,
    postNotificationUpdateUserDefinedNotification,
    postNotificationGetMyUserDefinedNotifications,
    postNotificationQueryMyUserDefinedNotifications,
} from '@apis/Notification';
import {
    IndexedNotification,
    NotificationTypePreference,
    Query,
    UserDefinedNotification,
    UserDefinedNotificationRecipient,
    UserDefinedNotificationRecipientMessageTypeItem,
} from '@apis/Notification/model';
import { UserDefinedNotificationRequest } from '@apis/Notification/model/userDefinedNotificationRequest';
import { QueryResult } from '@apis/Resources';
import { inject, injectable } from 'tsyringe';
import { exprBuilder, groupExprs, IFluentOperators, IStringFluentOperators, queryBuilder } from '../QueryExpr';
import { NotificationViewerService } from './NotificationViewerService';

export class UserDefinedNotificationApiService {
    public async getNotificationDefinitions() {
        return await getNotificationGetMyNotificationTypePreferences();
    }

    public async saveNotificationDefinition(definition: NotificationTypePreference) {
        return await postNotificationSaveNotificationTypePreference(definition);
    }

    public async saveNotificationRecipients(definitionId: number, recipients: UserDefinedNotificationRecipient[]) {
        return await postNotificationSaveNotificationRecipients(recipients, { preferenceId: definitionId });
    }

    public async getNotifications(options: UserDefinedNotificationRequest) {
        return await postNotificationGetMyUserDefinedNotifications(options);
    }

    public async getNotificationDefinitionById(id: number) {
        const definitions = await this.getNotificationDefinitions();
        return definitions.find((d) => d.Id === id);
    }

    public async getNotificationById(notificationId: number, recipientUserIds?: number[]) {
        const notifications = await this.getNotifications({ NotificationIds: [notificationId], RecipientUserIds: recipientUserIds });
        return notifications.find(() => true);
    }

    public async queryNotifications<T = IndexedNotification>(query: Query) {
        const { builder: xb } = exprBuilder<IndexedNotification>();
        query.Where = groupExprs('and', [query.Where, xb.resolve(xb.model.Queued!.eq(false))]);
        const response = await postNotificationQueryMyUserDefinedNotifications(query);
        return response as QueryResult<T>;
    }

    public queryFieldValues<T>(field: keyof IndexedNotification) {
        return async (filter?: string) => {
            const builder = queryBuilder<IndexedNotification>();
            if (filter) {
                builder.where((b) => (b.model[field] as unknown as IStringFluentOperators).contains(filter));
            }
            const response = await builder.select((b) => ({ value: b.model[field], ct: b.count() })).execute(this.queryNotifications);
            const result = response.Results?.map((r) => r.value as unknown as T) ?? [];
            return result.sort();
        };
    }
}

@injectable()
export class InAppNotificationViewTrackingService {
    public constructor(@inject(NotificationViewerService) private readonly viewerSvc: NotificationViewerService) {}

    public async dismissNotfication(notificationId: number) {
        await this.updateNotification(notificationId, { IsOpened: true });
    }

    public async markNotificationAsRead(notificationId: number) {
        const result = await this.updateNotification(notificationId, { IsOpened: true });
        this.viewerSvc.onRead.emit(result);
        return result;
    }

    private async updateNotification(notificationId: number, update: { IsDismissed?: boolean; IsOpened?: boolean }) {
        return await postNotificationUpdateUserDefinedNotification({
            ...update,
            UserDefinedNotificationId: notificationId,
        });
    }
}
